<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive } from "vue";
import { getKennungValue, getValue } from "@/DataHelper";
import { mountedBase, onSave, BaseSetup, onDelete } from "@/DataLoaderStatic";
import { getSiteData } from "@/NavigationHelper";
import { showSnackbar } from "@/UIHelper";
import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
import BelegWarnings from "./Template/BelegWarnings.vue";
import BelegImage from "./Template/BelegImage.vue";
import BelegDetails from "./Template/BelegDetails.vue";
import BelegKostenarten from "./Template/BelegKostenarten.vue";

const validator = createEditorValidator();
provideEditorValidator(validator);

const emit = defineEmits(["back", "save"]);

let saveVisible = ref<boolean>(false);
let setKey = ref<number>(0);
let warningsKey = ref<number>(0);
let readOnlyMutable = ref<boolean>(false);
// PDFs brauchen mehr Breite und stehen daher unter den Fehlermeldungen statt daneben
let isPdf = ref<boolean>(false);
function onImageIsPdf(value: boolean) {
  isPdf.value = value;
}

const pageParams: DataPageParameter = {
  backOnSave: true,
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_EditBeleg_Belegerkennung",
  aic: AUApi.Current.user.aic,
  emitBack: () => emit("back"),
};
const siteData = getSiteData();
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));

onMounted(async () => {
  pageParams.queryAIC = siteData.aic;
  pageParams.aic = siteData.aic;
  pageParams.bewegungsdaten = false;

  await mountedBase(uiData, pageParams, false);

  if (!uiData.showLoading && uiData.data) {
    const bezColumn = uiData.columns.find(
      (c: any) => c.kennung === "BEZEICHNUNG",
    );
    if (bezColumn) {
      const text = getKennungValue(
        uiData.data[0],
        "BEZEICHNUNG",
        uiData.columns,
      );
      if (!text) {
        uiData.data[0][bezColumn.name] = "in Bearbeitung";
      }
    }
    saveVisible.value = true;
  }

  // Delete/Speichern haengen wie in BaseEditor von NOCHANGE/allowDelete/save-Spalten ab
  const defColumsNoChange = uiData.columns.filter(
    (c: any) => c.kennung === "NOCHANGE",
  );
  const defColumsAllowDelete: any = uiData.columns.filter(
    (c: any) => c.meta.kz === "allowDelete",
  );
  if (defColumsNoChange && defColumsNoChange?.length > 0) {
    const noChange = getValue(uiData.data[0], defColumsNoChange[0]);
    if (noChange) {
      readOnlyMutable.value = true;
    }
  }

  if (defColumsAllowDelete && defColumsAllowDelete?.length > 0) {
    const allowDelete = getValue(uiData.data[0], defColumsAllowDelete[0]);
    if (!allowDelete) {
      readOnlyMutable.value = true;
    }
  }

  const defsave: any = uiData.columns.filter((c: any) => c.meta.kz === "save");
  if (defsave && defsave?.length > 0) {
    const allowSave = getValue(uiData.data[0], defsave[0]);
    if (!allowSave) {
      readOnlyMutable.value = true;
    }
  }

  uiData.showLoading = false;
  uiData.initialized = true;
});

function checkChanged() {
  saveVisible.value = true;
  setKey.value += 1;
}

async function onSaveBeleg() {
  if (readOnlyMutable.value) {
    return;
  }
  const { valid, invalidFields } = await validator.validateAsync();
  if (!valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" +
        invalidFields.join(", ") +
        ")",
      duration: 4000,
      color: "error",
    });
    return;
  }
  if (!siteData.reloadData) {
    siteData.reloadData = true;
  }
  const nodeRequest = uiData.saveNodeRequest ?? "";
  const aic = pageParams.aic ?? 0;

  onSave(
    uiData,
    pageParams,
    0,
    "",
    undefined,
    aic,
    true,
    [],
    nodeRequest,
  )?.then(() => {
    saveVisible.value = false;
    setKey.value += 1;
    emit("back");
  });
}

async function bewDelete() {
  if (readOnlyMutable.value) {
    return;
  }
  if (uiData.data && uiData.data[0].aic_Stamm) {
    if (
      confirm(
        "Wollen Sie den Datensatz wirklich - mit allen Kostensätzen löschen?",
      )
    ) {
      await onDelete(
        uiData,
        pageParams,
        uiData.data[0].aic_Stamm,
        "",
        undefined,
        true,
      );
      if (!siteData.reloadData) {
        siteData.reloadData = true;
      }
      emit("back");
    }
  }
}
</script>
<template>
  <v-card :loading="uiData.showLoading">
    <v-card-text v-if="uiData.initialized">
      <div class="beleg-grid" :class="{ 'beleg-grid-pdf': isPdf }">
        <div class="beleg-section grid-warn">
          <BelegWarnings
            :key="warningsKey"
            :zeitbereich="uiData.zeitbereich"
            :queryAic="pageParams.queryAIC"
          />
        </div>
        <div class="beleg-section grid-image">
          <div class="beleg-section-title pt-4">Bild</div>
          <BelegImage
            :largePreview="true"
            @changeValue="checkChanged"
            @isPdf="onImageIsPdf"
          />
        </div>
        <div class="beleg-section grid-details">
          <div class="beleg-section-title pt-4 pb-2">Detailinfos</div>
          <BelegDetails
            :readOnlyMutable="readOnlyMutable"
            @changeValue="checkChanged"
          />
        </div>
        <div class="beleg-section grid-kostenarten">
          <BelegKostenarten
            :zeitbereich="uiData.zeitbereich"
            :queryAic="pageParams.aic"
            @onReloadData="warningsKey += 1"
          />
        </div>
      </div>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :key="setKey"
        v-if="uiData.initialized"
        :deleteVisible="!readOnlyMutable"
        :createVisible="false"
        :refreshVisible="false"
        :backVisible="false"
        @delete="bewDelete"
        :saveVisible="!readOnlyMutable && saveVisible"
        @save="onSaveBeleg"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.beleg-section-title {
  font-weight: 600;
  padding-bottom: 4px;
}
.beleg-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas: "warn warn" "details image" "kostenarten kostenarten";
  column-gap: 16px;
}
.beleg-grid-pdf {
  grid-template-columns: 1fr;
  grid-template-areas: "warn" "image" "details" "kostenarten";
}
.grid-warn {
  grid-area: warn;
}
.grid-image {
  grid-area: image;
}
.grid-details {
  grid-area: details;
}
.grid-kostenarten {
  grid-area: kostenarten;
}
@media (max-width: 960px) {
  .beleg-grid {
    grid-template-columns: 1fr;
    grid-template-areas: "warn" "image" "details" "kostenarten";
  }
}
</style>
